/*
 * Assign flow — `assign.flow.*` (App-shell canon §7.3, FLEXIBLE).
 * Who → (Age) → (Guardian) → Details → Confirm → Done, with the DYNAMIC step
 * list computed in assignModel.ts. Transcribed from the canon §7.3 chrome +
 * testid table + the DC source (App Shell.dc.html assignVals). Canon wins.
 *
 * ROUTE (canon §1.2): /orders/:orderRef/tickets/:ticketId/seats/:seatNo/assign
 * — wizard sub-steps are in-flow state, not routed.
 *
 * DOM ids (canon §1.3): assign.root / assign.head / assign.body / assign.foot
 * / assign.step.{who|age|guardian|details|confirm} / assign.done.
 *
 * MUST-NOTS enforced (§7.3): a minor-only ticket offers ONLY the child choice;
 * NOTHING is sent before the explicit confirm (the live submit runs only from
 * the confirm button); a minor's waiver/payment is attributed to the guardian,
 * never the minor; SIGNING_AGE / age bands arrive via TicketPolicy — organiser
 * + jurisdiction data, not UI constants (see assignModel.resolveTicketPolicy).
 *
 * DRAFT SCOPING (§7.3 contract): the draft is reset AND stamped with the seat
 * (`{ticketId}.{seatNo}`) — a stale answer can never apply to another place.
 *
 * DATA (ruling A): live submits go to my-api (self-holder bootstrap / email
 * invite; a re-assign revokes first). Child + minor-guardian submissions need
 * the V1.1 child/guardian record contract (§12.2) — they fail HONESTLY at
 * confirm instead of fabricating a done state.
 */
import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { loadSession } from '../../auth/session'
import { getProfile } from '../../auth/cognitoProfile'
import { LiveDataNotice, useData } from '../../data/DataProvider'
import {
  allocateTicket, ensureSelfHolder, myApiBase, revokeAllocation, MyApiError,
} from '../../data/myApi'
import { useShellChrome } from '../../components/AppShell'
import {
  addChildHint, ageOnRaceDay, ageVerdict, assigneeName, bandNote, blockText, choices,
  confirmLabel, detailChrome, doneHeading, doneSub, emailHintLine, emptyDraft,
  genderEligible, GUARDIAN_OPTS, heading, holderNote, intro, isMinorAge, MONTHS, nextNote,
  splitCards, stepCounter, stepList, stepOk, summaryRows, resolveTicketPolicy,
  EYEBROWS, type AssignCtx, type AssignDraft, type ManagedChildRef, type TicketPolicy,
} from './assignModel'
import { holderLabel } from './ordersModel'
import { useOrderSeats } from './useOrderSeats'
import { bumpReadinessRefresh } from '../../data/getReady'

// ---------------------------------------------------------------------------
// Result handed to the submit seam at explicit confirm (never earlier, §7.3)
// ---------------------------------------------------------------------------

export interface AssignResult {
  seatKey: string
  mode: 'me' | 'other' | 'child'
  /** True when the seat already had a holder (replace semantics). */
  reassign: boolean
  email: string
  name: string
  minor: boolean
  guardianMode: '' | 'full' | 'shared'
  teenEmail: string
  childKey: string
  childName: string
}

// ---------------------------------------------------------------------------
// Styles (canon §2 tokens / DC transcription — same flow frame as waiver)
// ---------------------------------------------------------------------------

const h1Style: CSSProperties = { fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px' }
const introStyle: CSSProperties = { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 18px' }
const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 4,
}
const lblStyle: CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }
const inputStyle = (tone: 'idle' | 'ok' | 'bad' = 'idle'): CSSProperties => ({
  width: '100%', minHeight: 48, borderRadius: 11, padding: '0 13px', fontSize: 15,
  background: 'var(--bg-frame)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
  border: `1px solid ${tone === 'idle' ? 'var(--hairline-14)' : tone === 'ok' ? 'rgba(18,196,172,.55)' : 'var(--error-border)'}`,
})
const hintStyle = (tone: 'idle' | 'ok' | 'bad'): CSSProperties => ({
  fontSize: 11.5, marginTop: 7,
  ...(tone === 'idle' ? { color: 'var(--text-faint)' } : tone === 'ok' ? { color: 'var(--success)', fontWeight: 700 } : { color: 'var(--error)', fontWeight: 700 }),
})
const selStyle: CSSProperties = {
  width: '100%', minWidth: 0, minHeight: 48, border: '1px solid var(--hairline-14)', borderRadius: 11,
  padding: '0 8px', fontSize: 14, background: 'var(--bg-frame)', color: 'var(--text)', outline: 'none',
  fontFamily: 'inherit', appearance: 'none',
}
const cardBtnStyle = (on: boolean): CSSProperties => ({
  display: 'block', width: '100%', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
  padding: '13px 14px', marginBottom: 10, borderRadius: 13, background: 'var(--bg-frame)',
  color: 'var(--text)', border: `1px solid ${on ? 'rgba(18,196,172,.5)' : 'var(--hairline-12)'}`,
})
const backBtnStyle: CSSProperties = {
  flex: 'none', border: '1px solid var(--hairline-14)', background: 'transparent',
  color: 'var(--text-softbtn)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
  cursor: 'pointer', minHeight: 48, borderRadius: 12, padding: '0 16px',
}
const ctaStyle = (ok: boolean): CSSProperties => ({
  flex: 1, border: 0, borderRadius: 12, minHeight: 48, background: 'var(--accent)',
  color: 'var(--accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
  ...(ok ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }),
})
const verdictStyle = (tone: 'bad' | 'minor' | 'ok'): CSSProperties => ({
  fontSize: 11.5, lineHeight: 1.5, marginTop: 12, padding: '11px 13px', borderRadius: 11, fontWeight: 700,
  ...(tone === 'bad'
    ? { color: 'var(--error)', background: 'rgba(229,83,61,.09)', border: '1px solid rgba(229,83,61,.32)' }
    : tone === 'minor'
      ? { color: 'var(--warn)', background: 'rgba(230,193,90,.09)', border: '1px solid rgba(230,193,90,.3)' }
      : { color: 'var(--success)', background: 'rgba(18,196,172,.08)', border: '1px solid rgba(18,196,172,.28)' }),
})

// ---------------------------------------------------------------------------
// Flow component (presentational + in-flow step state; tests drive it directly)
// ---------------------------------------------------------------------------

export function AssignFlow({ ctx, seatKey, policy, raceDay, kids, onSubmit, onExit, onDone, onGetReady }: {
  ctx: AssignCtx
  /** `{ticketId}.{seatNo}` — stamps the draft (§7.3 scoping rule). */
  seatKey: string
  policy: TicketPolicy
  /** Race day for age-on-race-day. Live seam: the platform's order payloads
   *  carry no machine race date yet — irrelevant while live tickets resolve
   *  unbanded (no age step); tests pass the fixture date. */
  raceDay: Date
  kids: ManagedChildRef[]
  /** Runs ONLY from the explicit confirm button (§7.3 must-not). Rejection
   *  keeps the flow on confirm with an honest error. */
  onSubmit: (result: AssignResult) => Promise<{ inviteEmailSent?: boolean } | void>
  onExit: () => void
  onDone: () => void
  /** Optional "Get ready now" affordance on the DONE screen — offered only for
   *  the self ('me') assign (the path whose place lands in the account's Get
   *  ready). Absent ⇒ the DONE foot keeps its single "Back to the order" button
   *  (unit tests that omit it are unaffected). Live: routes to /get-ready
   *  (BOARDROOM-2026-08-14-100130). */
  onGetReady?: () => void
}) {
  const { isMobile } = useShellChrome()
  const [draft, setDraft] = useState<AssignDraft>(() => emptyDraft(seatKey, policy))
  const [fs, setFs] = useState(0)
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Honesty: the done screen only claims an email was sent when the backend
  // actually sent one (my-api inviteEmailSent). Defaults true; the real flag
  // lands from the submit result. 'me'/child paths send nothing, so the email
  // claim never renders for them anyway.
  const [inviteEmailSent, setInviteEmailSent] = useState(true)

  // Draft-scoping (§7.3): answers only count for the seat they were given on.
  if (draft.seatKey !== seatKey) {
    setDraft(emptyDraft(seatKey, policy))
    setFs(0)
    setSubmitState('idle')
    setSubmitError(null)
  }

  const set = (patch: Partial<AssignDraft>) => setDraft((d) => ({ ...d, ...patch }))

  const age = ageOnRaceDay(draft, raceDay)
  const steps = stepList(draft, policy, age)
  const total = steps.length
  const done = fs >= total
  const cur = steps[Math.min(fs, total - 1)]
  const isMinor = draft.mode === 'other' && isMinorAge(age, policy)
  const hasKids = kids.length > 0
  const adding = draft.addKid || !hasKids
  const ok = stepOk(cur, draft, policy, age, hasKids)
  const showBlock = !done && !ok
  const isLast = !done && fs === total - 1

  // Self-assign gender guard (CMT-2026-08-11-010806). The only gender known at
  // assign time is the account holder's own, so this gates ONLY mode 'me'.
  // Conservative — blocks a CLEAR binary-opposite mismatch only (see
  // assignModel.genderEligible); Open/Mixed tickets and unknown/self-described
  // holders are never blocked. The invite-by-email and child paths are out of
  // scope (their gender isn't known here — flagged separately).
  const selfGender = draft.mode === 'me'
    ? genderEligible(ctx.myGender, ctx.ticketType)
    : { ok: true as const, reason: undefined }
  const genderBlocked = !selfGender.ok

  const band = bandNote(policy)
  const dc = detailChrome(isMinor)
  const emailHint = emailHintLine(draft.email, dc.emailHint)
  const teenHint = emailHintLine(draft.teenEmail, 'Their own address — they set their own password')
  const verdict = ageVerdict(age, policy)
  const rows = summaryRows(draft, ctx, policy, age, kids)

  const back = () => {
    if (fs === 0) onExit()
    else setFs((n) => n - 1)
  }
  const goNext = () => { if (ok) setFs((n) => n + 1) }
  const submit = () => {
    if (!ok || submitState === 'submitting') return
    // Gender guard: never call the allocation API on a clear self-assign
    // mismatch — surface the reason on confirm (same honest-error UX as a
    // rejected submit), API untouched.
    if (genderBlocked) {
      setSubmitState('error')
      setSubmitError(selfGender.reason ?? 'This ticket is restricted and your profile gender does not match.')
      return
    }
    const childName = assigneeName(draft, ctx, kids, false)
    const result: AssignResult = {
      seatKey,
      mode: draft.mode as AssignResult['mode'],
      reassign: !!ctx.holder,
      email: draft.email.trim(),
      name: draft.name.trim(),
      minor: isMinor,
      guardianMode: isMinor ? draft.gMode : '',
      teenEmail: isMinor && draft.gMode === 'shared' ? draft.teenEmail.trim() : '',
      childKey: draft.mode === 'child' && !adding ? draft.child : '',
      childName: draft.mode === 'child' ? childName : '',
    }
    setSubmitState('submitting')
    setSubmitError(null)
    onSubmit(result)
      .then((res) => {
        // Only the invite (other-adult) path returns a send flag; me/child send
        // no email. Absent flag ⇒ true so the non-email done copy is unaffected.
        setInviteEmailSent(res && typeof res.inviteEmailSent === 'boolean' ? res.inviteEmailSent : true)
        setSubmitState('idle')
        setFs(total) // ASSIGNED
      })
      .catch((e) => {
        setSubmitState('error')
        setSubmitError(e instanceof Error ? e.message : 'The assignment could not be completed.')
      })
  }

  // Flow frame — cancels the shell content padding (same as wizard/waiver).
  const pad = isMobile
    ? { margin: '-18px -16px -26px', height: 'calc(100% + 44px)' }
    : { margin: '-26px -30px -36px', height: 'calc(100% + 62px)' }
  const headPad = isMobile ? '12px 16px 10px' : '18px 26px 12px'
  const bodyPad = isMobile ? '16px 16px 20px' : '20px 26px 24px'
  const footPad = isMobile ? '10px 16px 12px' : '14px 26px 16px'
  const blockPadX = isMobile ? 16 : 26

  const baseYear = raceDay.getFullYear()
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1))
  const years = Array.from({ length: 90 }, (_, i) => String(baseYear - i))

  return (
    <div id="assign.root" style={{ ...pad, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* HEAD — ‹ close · ticket context · Step {n} of {N} / ASSIGNED · progress */}
      <div id="assign.head" style={{ flex: 'none', padding: headPad, borderBottom: '1px solid var(--hairline-07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <button onClick={onExit} aria-label="Close" style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: '0 4px' }}>‹</button>
          <span data-testid="assign-context" style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.ticketType}</span>
          <span data-testid="assign-stepnum" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', flex: 'none' }}>{stepCounter(fs, total, done)}</span>
        </div>
        <div style={{ height: 5, borderRadius: 20, background: 'var(--progress-track, rgba(255,255,255,.08))', overflow: 'hidden', marginTop: 10 }}>
          <div data-testid="assign-progress" style={{ height: '100%', borderRadius: 20, background: 'var(--accent)', width: `${Math.min(((fs + 1) / total) * 100, 100)}%` }} />
        </div>
      </div>

      {/* BODY */}
      <div id="assign.body" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: bodyPad }}>
        {!done && (
          <>
            <div data-testid="assign-eyebrow" style={eyebrowStyle}>{EYEBROWS[cur]}</div>
            <h1 data-testid="assign-heading" style={h1Style}>{heading(cur, draft, ctx, age, adding, isMinor)}</h1>
            <p data-testid="assign-intro" style={introStyle}>{intro(cur, draft, ctx, policy, isMinor)}</p>
          </>
        )}

        {/* re-assign warning (§7.3) */}
        {!done && ctx.holder && (
          <div data-testid="assign-reassign-note" style={{ display: 'flex', gap: 9, padding: '11px 13px', marginBottom: 16, borderRadius: 12, background: 'rgba(230,193,90,.09)', border: '1px solid rgba(230,193,90,.3)' }}>
            <span aria-hidden style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
            <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--warn)', fontWeight: 700 }}>{holderNote(ctx.holder)}</span>
          </div>
        )}

        {/* ---- WHO ---- */}
        {!done && cur === 'who' && (
          <div id="assign.step.who">
            {band && (
              <div
                data-testid="assign-band-note"
                data-tone={band.tone}
                style={{
                  display: 'flex', gap: 9, padding: '11px 13px', marginBottom: 16, borderRadius: 12,
                  ...(band.tone === 'child'
                    ? { background: 'var(--tint-child)', border: '1px solid rgba(240,165,140,.3)' }
                    : { background: 'rgba(255,255,255,.04)', border: '1px solid var(--hairline-10)' }),
                }}
              >
                <span aria-hidden style={{ flex: 'none', fontSize: 13, color: band.tone === 'child' ? 'var(--child)' : 'var(--text-muted)' }}>◈</span>
                <span style={{ fontSize: 11.5, lineHeight: 1.5, fontWeight: 600, color: band.tone === 'child' ? 'var(--child)' : 'var(--text-secondary)' }}>{band.text}</span>
              </div>
            )}
            {choices(policy, ctx.myName).map((c) => (
              <button
                key={c.mode}
                data-testid="assign-choice"
                data-mode={c.mode}
                aria-pressed={draft.mode === c.mode}
                onClick={() => set({ mode: c.mode })}
                style={{
                  ...cardBtnStyle(draft.mode === c.mode),
                  display: 'grid', gridTemplateColumns: '26px minmax(0,1fr)', columnGap: 11, alignItems: 'center',
                }}
              >
                <span aria-hidden style={{
                  width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 12,
                  ...(draft.mode === c.mode
                    ? { background: 'var(--accent)', color: 'var(--accent-ink)', fontWeight: 800 }
                    : { background: 'rgba(255,255,255,.06)', color: 'var(--text-muted)' }),
                }}>{c.icon}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>{c.label}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 }}>{c.note}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ---- AGE ---- */}
        {!done && cur === 'age' && (
          <div id="assign.step.age">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: 8 }}>
              <select data-testid="assign-dob-day" aria-label="Day" value={draft.dobD || 'Day'} onChange={(e) => set({ dobD: e.target.value === 'Day' ? '' : e.target.value })} style={selStyle}>
                {['Day', ...days].map((o) => <option key={o}>{o}</option>)}
              </select>
              <select data-testid="assign-dob-month" aria-label="Month" value={draft.dobM || 'Month'} onChange={(e) => set({ dobM: e.target.value === 'Month' ? '' : e.target.value })} style={selStyle}>
                {['Month', ...MONTHS].map((o) => <option key={o}>{o}</option>)}
              </select>
              <select data-testid="assign-dob-year" aria-label="Year" value={draft.dobY || 'Year'} onChange={(e) => set({ dobY: e.target.value === 'Year' ? '' : e.target.value })} style={selStyle}>
                {['Year', ...years].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            {verdict && (
              <div data-testid="assign-age-verdict" data-tone={verdict.tone} style={verdictStyle(verdict.tone)}>{verdict.text}</div>
            )}
          </div>
        )}

        {/* ---- GUARDIAN ---- */}
        {!done && cur === 'guardian' && (
          <div id="assign.step.guardian">
            {GUARDIAN_OPTS.map((o) => (
              <button
                key={o.gMode}
                data-testid="assign-guardian-opt"
                data-mode={o.gMode}
                aria-pressed={draft.gMode === o.gMode}
                onClick={() => set({ gMode: o.gMode })}
                style={{ ...cardBtnStyle(draft.gMode === o.gMode), padding: '14px 15px', borderRadius: 14 }}
              >
                <span style={{ display: 'block', fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 5, color: draft.gMode === o.gMode ? 'var(--text)' : 'var(--text-softbtn)' }}>{o.title}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{o.note}</span>
                <span className="mono" style={{ display: 'block', fontSize: 9, letterSpacing: '.08em', color: 'var(--text-dim)', marginTop: 7 }}>{o.best}</span>
              </button>
            ))}
            {draft.gMode === 'shared' && (
              <div style={{ marginTop: 6 }}>
                <label style={lblStyle}>Their email</label>
                <input
                  data-testid="assign-teen-email"
                  value={draft.teenEmail}
                  onChange={(e) => set({ teenEmail: e.target.value })}
                  placeholder="them@example.com"
                  style={inputStyle(draft.teenEmail ? teenHint.tone : 'idle')}
                />
                <div data-testid="assign-teen-hint" style={hintStyle(teenHint.tone)}>{teenHint.text}</div>
              </div>
            )}
            {draft.gMode && (
              <div style={{ display: 'grid', gap: 9, marginTop: 16 }}>
                {splitCards(draft.gMode, ctx.myName, draft.name).map((p) => (
                  <div key={p.side} data-testid="assign-split-card" data-side={p.side} style={{ background: 'rgba(255,255,255,.035)', border: '1px solid var(--hairline-08)', borderRadius: 13, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 800, overflowWrap: 'anywhere' }}>{p.who}</span>
                      <span className="mono" style={{ flex: 'none', fontSize: 8.5, letterSpacing: '.09em', color: 'var(--text-muted)', border: '1px solid var(--hairline-14)', borderRadius: 4, padding: '2px 5px' }}>{p.tag}</span>
                    </div>
                    {p.items.map((i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px minmax(0,1fr)', columnGap: 7, padding: '2px 0' }}>
                        <span aria-hidden style={{ color: 'var(--success)', fontSize: 10, paddingTop: 3 }}>•</span>
                        <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{i}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---- DETAILS ---- */}
        {!done && cur === 'details' && (
          <div id="assign.step.details">
            {draft.mode === 'other' && (
              <>
                <label style={lblStyle}>{dc.emailLabel}</label>
                <input
                  data-testid="assign-email"
                  value={draft.email}
                  onChange={(e) => set({ email: e.target.value })}
                  placeholder="name@example.com"
                  style={inputStyle(draft.email ? emailHint.tone : 'idle')}
                />
                <div data-testid="assign-email-hint" style={hintStyle(emailHint.tone)}>{emailHint.text}</div>
                <div style={{ height: 16 }} />
                <label style={lblStyle}>{dc.nameLabel}</label>
                <input
                  data-testid="assign-name"
                  value={draft.name}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder={dc.namePlaceholder}
                  style={inputStyle()}
                />
              </>
            )}
            {draft.mode === 'child' && (
              // TODO(V1.1 kids §12.2): the child-record store (shared with
              // Profile → Managed profiles and the child-subject wizard) is
              // the V1.1 amendment's contract; kids arrive via the prop seam.
              !adding ? (
                <>
                  {kids.map((k) => (
                    <button
                      key={k.key}
                      data-testid="assign-kid-card"
                      data-child={k.key}
                      aria-pressed={draft.child === k.key}
                      onClick={() => set({ child: k.key, name: k.name, addKid: false })}
                      style={cardBtnStyle(draft.child === k.key)}
                    >
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>{k.name}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>
                        {k.profileComplete ? 'On your account · profile complete' : 'On your account · profile to finish'}
                      </span>
                    </button>
                  ))}
                  <button
                    data-testid="assign-add-child"
                    onClick={() => set({ addKid: true, child: '' })}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', minHeight: 48, border: '1.5px dashed rgba(255,255,255,.18)', borderRadius: 13, background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', padding: '0 14px' }}
                  >
                    <span aria-hidden style={{ flex: 'none', color: 'var(--accent-bright)', fontSize: 16 }}>+</span>
                    <span>Add a child</span>
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>{addChildHint(hasKids)}</div>
                  <label style={lblStyle}>First name</label>
                  <input data-testid="assign-kid-first" value={draft.kidFirst} onChange={(e) => set({ kidFirst: e.target.value })} placeholder="Mia" style={inputStyle()} />
                  <div style={{ height: 14 }} />
                  <label style={lblStyle}>Last name</label>
                  <input data-testid="assign-kid-last" value={draft.kidLast} onChange={(e) => set({ kidLast: e.target.value })} placeholder="Diaz" style={inputStyle()} />
                  {hasKids && (
                    <>
                      <div style={{ height: 14 }} />
                      <button
                        data-testid="assign-kid-back"
                        onClick={() => set({ addKid: false, kidFirst: '', kidLast: '' })}
                        style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}
                      >‹ Choose an existing child instead</button>
                    </>
                  )}
                </>
              )
            )}
          </div>
        )}

        {/* ---- CONFIRM ---- */}
        {!done && cur === 'confirm' && (
          <div id="assign.step.confirm">
            <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 14, padding: '4px 15px', marginBottom: 14 }}>
              {rows.map((r) => (
                <div key={r.k} data-testid="assign-summary-row" data-k={r.k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--hairline-06)' }}>
                  <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-muted)' }}>{r.k}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', overflowWrap: 'anywhere' }}>{r.v}</span>
                </div>
              ))}
            </div>
            <div data-testid="assign-next-note" style={{ fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.5 }}>{nextNote(draft, isMinor)}</div>
            {genderBlocked && (
              <div data-testid="assign-gender-block" role="alert" style={verdictStyle('bad')}>{selfGender.reason}</div>
            )}
            {submitError && !genderBlocked && (
              <div role="alert" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--error)', marginTop: 12 }}>{submitError}</div>
            )}
          </div>
        )}

        {/* ---- DONE ---- */}
        {done && (
          <div id="assign.done">
            <div style={{ textAlign: 'center', padding: '6px 0 4px' }}>
              <div aria-hidden style={{ width: 58, height: 58, borderRadius: '50%', background: 'rgba(18,196,172,.16)', color: 'var(--accent-bright)', display: 'grid', placeItems: 'center', fontSize: 27, margin: '0 auto 14px' }}>✓</div>
              <h1 data-testid="assign-done-heading" style={h1Style}>{doneHeading(draft, ctx, isMinor, inviteEmailSent)}</h1>
              <p data-testid="assign-done-sub" style={{ ...introStyle, margin: '0 0 18px' }}>{doneSub(draft, ctx, kids, isMinor, inviteEmailSent)}</p>
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 14, padding: '4px 15px' }}>
              {rows.map((r) => (
                <div key={r.k} data-testid="assign-summary-row" data-k={r.k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--hairline-06)' }}>
                  <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-muted)' }}>{r.k}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right', overflowWrap: 'anywhere' }}>{r.v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* BLOCK BAR */}
      {showBlock && (
        <div data-testid="assign-block" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'rgba(230,193,90,.1)', borderTop: '1px solid rgba(230,193,90,.28)' }}>
          <span aria-hidden style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>{blockText(cur, draft, policy, hasKids)}</span>
        </div>
      )}

      {/* FOOT */}
      <div id="assign.foot" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: footPad, borderTop: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
        {done ? (
          // Self-assign lands the place in the account's Get ready — offer a
          // direct "Get ready now" CTA (primary) alongside "Back to the order"
          // so the buyer drops straight into their steps (BOARDROOM-2026-08-14-
          // 100130). Other paths (invite/child) keep the single order button.
          onGetReady && draft.mode === 'me' ? (
            <>
              <button data-testid="assign-done-close" onClick={onDone} style={backBtnStyle}>Back to the order</button>
              <button data-testid="assign-done-getready" onClick={onGetReady} style={ctaStyle(true)}>Get ready now</button>
            </>
          ) : (
            <button data-testid="assign-done-close" onClick={onDone} style={{ ...ctaStyle(true), width: '100%' }}>Back to the order</button>
          )
        ) : (
          <>
            <button data-testid="assign-back" onClick={back} style={backBtnStyle}>Back</button>
            {isLast ? (
              <button
                data-testid="assign-submit"
                onClick={submit}
                aria-disabled={!ok || genderBlocked || submitState === 'submitting'}
                style={ctaStyle(ok && !genderBlocked && submitState !== 'submitting')}
              >
                {submitState === 'submitting' ? 'Sending…' : confirmLabel(draft, isMinor)}
              </button>
            ) : (
              <button data-testid="assign-continue" onClick={goNext} aria-disabled={!ok} style={ctaStyle(ok)}>Continue</button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live route container
// ---------------------------------------------------------------------------

/** Live submit: my-api is the allocation authority. Self → self-holder
 *  bootstrap; other adult → email invite; re-assign revokes the standing
 *  allocation first. All of it runs ONLY at explicit confirm (§7.3).
 *
 *  A successful allocation mutation signals the readiness-refresh bus
 *  (bumpReadinessRefresh) so the live Get-ready + Tickets subscription re-reads
 *  holdings IMMEDIATELY — a self-assign otherwise only surfaced its new place
 *  after a manual browser refresh (the stale-cache live bug,
 *  BOARDROOM-2026-08-14-100130). Exported for the unit test that proves the bus
 *  fires on self-assign. */
export async function liveSubmit(
  orderId: string, ticketId: string, r: AssignResult,
): Promise<{ inviteEmailSent: boolean }> {
  const base = myApiBase()
  const session = loadSession()
  if (!base || !session) {
    throw new Error('The allocation service (my-api) is not connected in this build — nothing was sent.')
  }
  if (r.mode === 'child' || r.minor) {
    // TODO(V1.1 kids §12.2 + my-api child/guardian contract): child-record
    // creation, guardian waiver routing and teen-login invites have no server
    // contract yet — fail honestly, never fabricate a done state.
    throw new Error('Child and guardian assignments are not available yet — this arrives with the child-record release.')
  }
  try {
    if (r.reassign) await revokeAllocation(session.idToken, base, orderId, ticketId)
    if (r.mode === 'me') {
      // Self-holder bootstrap sends no email — the "emailed" claim never renders.
      await ensureSelfHolder(session.idToken, base, ticketId)
      // The new self-held place must appear in Get ready + Tickets with NO
      // manual refresh — signal the live subscription to re-read holdings now.
      bumpReadinessRefresh()
      return { inviteEmailSent: true }
    }
    // Other-adult invite: surface the backend's honest send flag so the done
    // screen never claims an email that wasn't sent (e.g. SendGrid off).
    const res = await allocateTicket(session.idToken, base, orderId, ticketId, r.email)
    // A re-assign may have revoked a place THIS account held (its Get-ready card
    // must drop), so refresh the live subscription on the invite path too.
    bumpReadinessRefresh()
    return { inviteEmailSent: res.inviteEmailSent }
  } catch (e) {
    throw new Error(
      e instanceof MyApiError && e.code === 'already_allocated'
        ? 'That seat already has a live allocation — revoke it first.'
        : e instanceof Error ? e.message : 'The allocation service returned an error.',
    )
  }
}

export function AssignEntry() {
  const { liveState, orders, sessionEmail } = useData()
  const { orderRef = '', ticketId = '', seatNo: seatNoParam = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const order = orders.find((o) => o.ref === orderRef) ?? null
  const { state, tickets, refresh } = useOrderSeats(order)

  // Account-holder Cognito gender for the self-assign eligibility guard (same
  // getProfile seam Profile.tsx uses). Best-effort: a failed/absent read leaves
  // gender undefined, which genderEligible treats as ALLOW (conservative).
  const [holderGender, setHolderGender] = useState<string | undefined>(undefined)
  useEffect(() => {
    const session = loadSession()
    if (!session?.accessToken) return
    let cancelled = false
    getProfile(session.accessToken)
      .then((p) => { if (!cancelled) setHolderGender(p.gender) })
      .catch(() => { /* unknown gender ⇒ never blocks (conservative) */ })
    return () => { cancelled = true }
  }, [])

  if (liveState !== 'ready') {
    return <div id="assign.root"><LiveDataNotice liveState={liveState} /></div>
  }
  const ticket = tickets.find((t) => t.id === ticketId) ?? null
  const seatNo = Number(seatNoParam)
  const seat = ticket?.seats.find((s) => s.seatNo === seatNo) ?? null
  const detailPath = `/orders/${orderRef}/tickets/${ticketId}`

  if (!order || !ticket || !seat || state !== 'ready') {
    // Honest miss / allocation service unreadable — assigning needs a live,
    // readable seat (nothing may be sent blind).
    return (
      <div id="assign.root" style={{ maxWidth: 'var(--content-max, 700px)' }}>
        <p role="status" className="secondary" style={{ fontSize: 13 }}>
          {state === 'loading'
            ? 'Loading this seat…'
            : 'This seat can’t be opened for assigning right now — it needs the live allocation service and a seat that belongs to your order.'}
        </p>
        <button
          onClick={() => navigate(detailPath)}
          style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}
        >‹ Back to the ticket</button>
      </div>
    )
  }

  // Account-holder identity: the mobile-shell name resolution (Cognito) is not
  // lifted to a provider yet; the session email is the honest fallback for the
  // `Myself — {name}` card. Route state may carry the resolved name.
  const routeName = (location.state as { accountName?: string } | null)?.accountName
  const ctx: AssignCtx = {
    ticketType: ticket.typeName,
    seatNo,
    of: ticket.seats.length,
    holder: seat.status !== 'unassigned' ? holderLabel(seat) : '',
    myName: routeName || sessionEmail || 'your account',
    myEmail: sessionEmail ?? '',
    myGender: holderGender,
  }

  return (
    <AssignFlow
      ctx={ctx}
      seatKey={`${ticketId}.${seatNo}`}
      // Live tickets resolve unbanded until the organiser ticket-type config
      // reaches the order payload (assignModel.resolveTicketPolicy TODOs).
      policy={resolveTicketPolicy()}
      raceDay={new Date()}
      kids={[] /* TODO(V1.1 kids §12.2): shared child-record store */}
      onSubmit={(r) => liveSubmit(order.id, ticketId, r)}
      onExit={() => navigate(detailPath)}
      onDone={() => {
        refresh()
        navigate(`/orders/${orderRef}`)
      }}
      // Self-assign drop-through: route straight into Get ready for the place
      // just taken. liveSubmit already bumped the readiness bus, so the card is
      // live by the time we land (BOARDROOM-2026-08-14-100130).
      onGetReady={() => navigate('/get-ready')}
    />
  )
}
